import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Package, 
  Clock, 
  CheckCircle, 
  Truck, 
  XCircle,
  CreditCard,
  ChevronRight
} from 'lucide-react';
import { ordersAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import { getImageUrl, formatPrice } from '../utils/helpers';

const MyPurchases = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadOrders();
    }
  }, [user]);

  const loadOrders = async () => {
    try {
      const data = await ordersAPI.getAll();
      setOrders(data);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      pending_payment: { 
        label: 'Pendiente de pago', 
        color: 'bg-yellow-100 text-yellow-800',
        icon: Clock
      },
      paid: { 
        label: 'Pagado', 
        color: 'bg-zl-orange-100 text-zl-orange-700',
        icon: CreditCard
      },
      shipped: { 
        label: 'Enviado', 
        color: 'bg-purple-100 text-purple-800',
        icon: Truck
      },
      delivered: { 
        label: 'Entregado', 
        color: 'bg-green-100 text-green-800',
        icon: CheckCircle
      },
      cancelled: { 
        label: 'Cancelado', 
        color: 'bg-red-100 text-red-800',
        icon: XCircle
      }
    };
    return statusMap[status] || statusMap.pending_payment;
  };

  const handleConfirmPayment = async (orderId) => {
    try {
      await ordersAPI.updateStatus(orderId, 'paid');
      toast({
        title: '¡Pago confirmado!',
        description: 'El vendedor ha sido notificado',
      });
      loadOrders();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo confirmar el pago',
        variant: 'destructive'
      });
    }
  };

  const handleConfirmDelivery = async (orderId) => {
    try {
      await ordersAPI.updateStatus(orderId, 'delivered');
      toast({
        title: '¡Entrega confirmada!',
        description: 'Gracias por tu compra',
      });
      loadOrders();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo confirmar la entrega',
        variant: 'destructive'
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center py-16">
        <Card className="p-8 text-center">
          <p className="mb-4">Debes iniciar sesion para ver tus compras</p>
          <Button onClick={() => navigate('/login')}>Iniciar sesion</Button>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <Package className="w-16 h-16 mx-auto text-gray-300 animate-pulse mb-4" />
          <p className="text-gray-600">Cargando compras...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
        <h1 className="text-3xl font-bold">Mis Compras</h1>

        {orders.length === 0 ? (
          <Card className="p-12 text-center">
            <Package className="w-24 h-24 mx-auto text-gray-300 mb-4" />
            <h2 className="text-2xl font-bold mb-2">No tienes compras</h2>
            <p className="text-gray-600 mb-6">
              ¡Explora nuestros productos y realiza tu primera compra!
            </p>
            <Button onClick={() => navigate('/')} className="bg-zl-orange hover:bg-zl-orange-600">
              Ir a comprar
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => {
              const statusInfo = getStatusInfo(order.status);
              const StatusIcon = statusInfo.icon;
              
              return (
                <Card key={order.order_id} className="p-6">
                  {/* Order Header */}
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 pb-4 border-b">
                    <div>
                      <p className="text-sm text-gray-500">
                        Pedido #{order.order_id?.substring(0, 8).toUpperCase()}
                      </p>
                      <p className="text-sm text-gray-500">
                        {formatDate(order.created_at)}
                      </p>
                    </div>
                    <Badge className={`${statusInfo.color} flex items-center gap-1 mt-2 md:mt-0`}>
                      <StatusIcon className="w-4 h-4" />
                      {statusInfo.label}
                    </Badge>
                  </div>

                  {/* Order Items */}
                  <div className="space-y-3 mb-4">
                    {order.items?.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-4">
                        <img
                          src={getImageUrl(item.product_image)}
                          alt={item.product_title}
                          className="w-20 h-20 object-cover rounded cursor-pointer hover:opacity-80"
                          onClick={() => navigate(`/product/${item.product_id}`)}
                          onError={(e) => e.target.src = 'https://via.placeholder.com/80'}
                        />
                        <div className="flex-1">
                          <p 
                            className="font-semibold hover:text-zl-orange cursor-pointer"
                            onClick={() => navigate(`/product/${item.product_id}`)}
                          >
                            {item.product_title}
                          </p>
                          <p className="text-sm text-gray-500">
                            Cantidad: {item.quantity}{item.variant_label ? ` · ${item.variant_label}` : ''} · Vendedor: {item.seller_name}
                          </p>
                        </div>
                        <p className="font-bold">{formatPrice(item.price * item.quantity)}</p>
                      </div>
                    ))}
                  </div>

                  {/* Payment Info for pending orders */}
                  {order.status === 'pending_payment' && order.payment_info && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                      <p className="font-semibold text-yellow-800 mb-2">Datos para transferencia:</p>
                      <div className="text-sm text-yellow-700 space-y-1">
                        {order.payment_info.bank_name && (
                          <p>Banco: <span className="font-semibold">{order.payment_info.bank_name}</span></p>
                        )}
                        {order.payment_info.account_holder && (
                          <p>Titular: <span className="font-semibold">{order.payment_info.account_holder}</span></p>
                        )}
                        {order.payment_info.phone_number && (
                          <p>Teléfono: <span className="font-semibold">{order.payment_info.phone_number}</span></p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Order Footer */}
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between pt-4 border-t">
                    <div>
                      <p className="text-lg font-bold">
                        Total: <span className="text-zl-orange">{formatPrice(order.total)}</span>
                      </p>
                    </div>
                    
                    <div className="flex gap-2 mt-3 md:mt-0">
                      {order.status === 'pending_payment' && (
                        <Button 
                          onClick={() => handleConfirmPayment(order.order_id)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Confirmar pago
                        </Button>
                      )}
                      
                      {order.status === 'shipped' && (
                        <Button 
                          onClick={() => handleConfirmDelivery(order.order_id)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Confirmar entrega
                        </Button>
                      )}

                      <Button 
                        variant="outline"
                        onClick={() => navigate(`/order/${order.order_id}`)}
                      >
                        Ver detalles
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
    </div>
  );
};

export default MyPurchases;
